import React, { useCallback, useMemo, useRef } from "react";

/**
 * Single-thumb range slider for discrete option sets.
 * Selects everything from the first option up to the thumb position.
 *
 * @param {Object[]} options    - Array of { label: string, value: any }
 * @param {any[]}    selected   - Currently selected values (contiguous from start)
 * @param {Function} onChange   - Called with the new selected values array
 * @param {string}   [color="secondary"] - Tailwind color token for active state
 * @param {boolean}  [showLabels=true]   - Show labels below the track
 * @param {boolean}  [showValue=true]    - Show the current value badge above the track
 * @param {string}   [className]         - Additional wrapper classes
 */
const RangeSlider = ({
  options = [],
  selected = [],
  onChange,
  color = "secondary",
  showLabels = true,
  showValue = true,
  className = "",
}) => {
  const max = options.length - 1;

  const thumbIdx = useMemo(() => {
    if (selected.length === 0 || max < 1) return 0;
    const idx = options.findIndex(
      (o) => o.value === selected[selected.length - 1],
    );
    return idx >= 0 ? idx : 0;
  }, [selected, options, max]);

  const emitUpTo = useCallback(
    (idx) => {
      const clamped = Math.max(0, Math.min(idx, max));
      onChange(options.slice(0, clamped + 1).map((o) => o.value));
    },
    [max, onChange, options],
  );

  const trackRef = useRef(null);

  const idxFromEvent = useCallback(
    (e) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return 0;
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const pct = Math.max(0, Math.min(x / rect.width, 1));
      return Math.round(pct * max);
    },
    [max],
  );

  const dragging = useRef(false);

  const startDrag = useCallback(
    (e) => {
      e.preventDefault();
      dragging.current = true;

      const move = (ev) => emitUpTo(idxFromEvent(ev));

      const up = () => {
        dragging.current = false;
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
        window.removeEventListener("touchmove", move);
        window.removeEventListener("touchend", up);
      };

      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
      window.addEventListener("touchmove", move);
      window.addEventListener("touchend", up);
    },
    [idxFromEvent, emitUpTo],
  );

  const handleTrackClick = useCallback(
    (e) => {
      if (dragging.current) return;
      emitUpTo(idxFromEvent(e));
    },
    [idxFromEvent, emitUpTo],
  );

  if (max < 1) return null;

  const thumbPct = (thumbIdx / max) * 100;
  const hasSelection = selected.length > 0;
  const currentLabel = options[thumbIdx]?.label ?? "";

  return (
    <div className={`flex flex-col gap-1.5 pt-1 pb-2 ${className}`}>
      {showValue && hasSelection && (
        <div className="flex items-center justify-between">
          <span
            className={`text-xs font-medium text-${color} bg-${color}-transparent px-2 py-1 rounded`}
          >
            Up to {currentLabel}
          </span>
        </div>
      )}

      <div className="px-2.5">
        <div
          ref={trackRef}
          className="relative h-10 cursor-pointer select-none"
          onClick={handleTrackClick}
        >
          <div className="absolute top-1/2 left-0 right-0 h-1.5 bg-gray-200 rounded-full -translate-y-1/2" />

          {hasSelection && (
            <div
              className={`absolute top-1/2 h-1.5 bg-${color} rounded-full -translate-y-1/2 left-0`}
              style={{ width: `${thumbPct}%` }}
            />
          )}

          <div
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-white border-2 border-${color} rounded-full shadow-md hover:scale-110 transition-transform z-20 cursor-grab active:cursor-grabbing`}
            style={{ left: `${thumbPct}%` }}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
          />
        </div>
      </div>

      {showLabels && (
        <div className="flex justify-between -mt-1 px-2.5">
          {options.map((opt, idx) => (
            <span
              key={opt.value}
              className={`text-[0.55rem] font-inter leading-none text-center cursor-pointer ${
                idx <= thumbIdx && hasSelection
                  ? `text-${color} font-medium`
                  : "text-gray-400"
              }`}
              onClick={() => emitUpTo(idx)}
            >
              {opt.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default RangeSlider;
